import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CreditGaugeProps {
  used: number;
  total: number;
  overspend?: number;
}

export function CreditGauge({ used, total, overspend = 0 }: CreditGaugeProps) {
  const effectiveTotal = total + overspend;
  const usedPercent =
    effectiveTotal > 0 ? Math.min((used / effectiveTotal) * 100, 100) : 0;
  const basePercent = effectiveTotal > 0 ? (total / effectiveTotal) * 100 : 100;
  const isOverspending = used > total;

  const barClass = isOverspending
    ? "bg-destructive"
    : total > 0 && used / total > 0.8
      ? "bg-amber-500"
      : "bg-primary";

  const tooltipLabel = `${used.toLocaleString()} used of ${total.toLocaleString()} total${
    overspend > 0 ? ` (+${overspend.toLocaleString()} overspend)` : ""
  }`;

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {used.toLocaleString()} / {total.toLocaleString()}
        </span>
        <span>{total > 0 ? Math.round((used / total) * 100) : 0}%</span>
      </div>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative h-6 w-full overflow-hidden rounded-md bg-muted">
              <div
                className={`h-full rounded-md transition-[width] duration-500 ease-out ${barClass}`}
                style={{ width: `${usedPercent}%` }}
              />
              {overspend > 0 && (
                <div
                  className="absolute top-0 h-full w-0.5 bg-foreground/60"
                  style={{ left: `${basePercent}%` }}
                />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>{tooltipLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
